/**
 * ANTLITZWERK · DAS RITUAL
 *
 * Adaptive product guidance for the isolated Ritual section. The customer sees
 * only clear questions that relate to the areas they selected. All evaluation
 * happens locally in the browser; answers are never transmitted or written to
 * persistent site storage. A completed result is kept only on its own browser
 * history entry while a recommended product is being viewed.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-awr-root]';
  var AUTO_ADVANCE_MS = 285;
  var STAGE_OUT_MS = 145;
  var STAGE_IN_MS = 310;
  var MAX_RECOMMENDATIONS = 5;
  var RETURN_HISTORY_KEY = '__antlitzwerkRitualReturn';
  var RETURN_HISTORY_VERSION = 1;

  var QUESTION_DEFINITIONS = [
    { id: 'focus', chapter: 'start', type: 'multi', max: 3, layout: 'three' },
    { id: 'dailyTime', chapter: 'start', type: 'single', layout: 'scale' },
    { id: 'stress', chapter: 'start', type: 'single', layout: 'scale' },

    { id: 'faceFeel', chapter: 'care', type: 'single', layout: 'three', when: hasFocus('face') },
    { id: 'faceGoals', chapter: 'care', type: 'multi', max: 2, layout: 'three', when: hasFocus('face') },
    {
      id: 'faceTexture',
      chapter: 'care',
      type: 'single',
      layout: 'scale',
      when: function (answers) {
        if (!contains(answers.focus, 'face')) return false;
        var goals = toArray(answers.faceGoals);
        return goals.some(function (goal) { return goal !== 'removal'; });
      }
    },
    { id: 'eyeNeed', chapter: 'care', type: 'single', layout: 'three', when: hasFocus('eyes') },
    { id: 'makeupUse', chapter: 'care', type: 'single', layout: 'three', when: hasFocus('expression') },
    { id: 'expressionGoal', chapter: 'care', type: 'single', layout: 'scale', when: hasFocus('expression') },
    {
      id: 'lipShade',
      chapter: 'care',
      type: 'single',
      layout: 'three',
      when: function (answers) {
        return contains(answers.focus, 'expression') &&
          ['lips', 'both', 'subtle'].indexOf(answers.expressionGoal) !== -1;
      }
    },
    { id: 'hairNeed', chapter: 'care', type: 'multi', max: 2, layout: 'three', when: hasFocus('hair') },
    { id: 'bodyNeed', chapter: 'care', type: 'multi', max: 2, layout: 'scale', when: hasFocus('body') },
    {
      id: 'bodyTexture',
      chapter: 'care',
      type: 'single',
      layout: 'scale',
      when: function (answers) {
        if (!contains(answers.focus, 'body')) return false;
        return toArray(answers.bodyNeed).some(function (need) {
          return ['dry', 'rough', 'glow'].indexOf(need) !== -1;
        });
      }
    },
    { id: 'eveningNeed', chapter: 'care', type: 'single', layout: 'three', when: hasFocus('calm') },
    {
      id: 'scentTolerance',
      chapter: 'care',
      type: 'single',
      layout: 'three',
      when: function (answers) {
        return contains(answers.focus, 'calm') ||
          (contains(answers.focus, 'body') && contains(answers.bodyNeed, 'cleanse'));
      }
    },

    {
      id: 'morning',
      chapter: 'everyday',
      type: 'single',
      layout: 'scale',
      when: function (answers) {
        return contains(answers.focus, 'eyes') || contains(answers.focus, 'calm');
      }
    },
    { id: 'currentRoutine', chapter: 'everyday', type: 'single', layout: 'scale' },
    { id: 'recommendationSize', chapter: 'everyday', type: 'single', layout: 'scale' },

    { id: 'avoid', chapter: 'measure', type: 'multi', max: 2, layout: 'three' },
    { id: 'priority', chapter: 'measure', type: 'single', layout: 'three' }
  ];

  var DEFINITION_MAP = QUESTION_DEFINITIONS.reduce(function (map, definition) {
    map[definition.id] = definition;
    return map;
  }, {});

  var PRODUCT_META = {
    zeitanmut: { domain: 'face', task: 'face-hydration', order: 10, usage: 20 },
    tagesnacht: { domain: 'face', task: 'face-cream', order: 20, usage: 30 },
    augenlicht: { domain: 'eyes', task: 'eye-care', order: 10, usage: 10 },
    lichtessenz: { domain: 'face', task: 'face-oil', order: 30, usage: 40 },
    enthuellung: { domain: 'face', task: 'makeup-removal', order: 5, usage: 10 },
    rosenanmut: { domain: 'expression', task: 'lip-colour', order: 10, usage: 20 },
    wimpernwerk: { domain: 'expression', task: 'lash-definition', order: 20, usage: 10 },
    seidenthron: { domain: 'hair', task: 'hair-cleanse', order: 10, usage: 10 },
    seidenkrone: { domain: 'hair', task: 'hair-condition', order: 20, usage: 20 },
    seelenbad: { domain: 'body', task: 'body-cleanse', order: 10, usage: 10 },
    hautfrieden: { domain: 'body', task: 'body-cream', order: 20, usage: 20 },
    koerperlicht: { domain: 'body', task: 'body-oil', order: 30, usage: 30 },
    seelenflamme: { domain: 'calm', task: 'atmosphere', order: 10, usage: 10 }
  };

  var PRODUCT_KEYS = Object.keys(PRODUCT_META);

  function hasFocus(focus) {
    return function (answers) { return contains(answers.focus, focus); };
  }

  function contains(value, needle) {
    return Array.isArray(value) ? value.indexOf(needle) !== -1 : value === needle;
  }

  function toArray(value) {
    if (Array.isArray(value)) return value.slice();
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function escapeHtml(value) {
    return String(value === undefined || value === null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#096;');
  }

  function decodeHtmlEntities(value) {
    var text = String(value === undefined || value === null ? '' : value);
    if (text.indexOf('&') === -1) return text;

    var textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  function storefrontRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = String(root || '/');
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function normalizeLocale(value) {
    var locale = String(value || 'de').toLowerCase().split('-')[0];
    return ['de', 'en', 'fr'].indexOf(locale) !== -1 ? locale : 'de';
  }

  function firstName(value) {
    var clean = String(value || '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return clean ? clean.split(' ')[0].slice(0, 24) : '';
  }

  function lowerFirst(value, locale) {
    var text = String(value || '').trim();
    if (!text) return '';
    return text.charAt(0).toLocaleLowerCase(locale || undefined) + text.slice(1);
  }

  function upperFirst(value, locale) {
    var text = String(value || '').trim();
    if (!text) return '';
    return text.charAt(0).toLocaleUpperCase(locale || undefined) + text.slice(1);
  }

  function replaceTokens(template, values) {
    return String(template || '').replace(/\{([^}]+)\}/g, function (match, key) {
      return values[key] !== undefined && values[key] !== null ? String(values[key]) : '';
    });
  }

  function stableStringify(value) {
    if (Array.isArray(value)) return '[' + value.map(stableStringify).join(',') + ']';
    if (value && typeof value === 'object') {
      return '{' + Object.keys(value).sort().map(function (key) {
        return JSON.stringify(key) + ':' + stableStringify(value[key]);
      }).join(',') + '}';
    }
    return JSON.stringify(value);
  }

  function hashString(value) {
    var hash = 2166136261;
    var text = String(value || '');
    for (var i = 0; i < text.length; i += 1) {
      hash ^= text.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  function pick(list, signature, salt) {
    if (!Array.isArray(list) || !list.length) return '';
    return list[hashString(signature + '|' + salt) % list.length];
  }

  function unique(list) {
    return list.filter(function (item, index) { return item && list.indexOf(item) === index; });
  }

  function listFormat(items, locale) {
    var clean = unique(items.filter(Boolean));
    if (!clean.length) return '';
    if (clean.length === 1) return clean[0];

    var language = String(locale || '').toLowerCase().split('-')[0];
    var last = clean[clean.length - 1];
    var embeddedConjunction =
      (language === 'de' && /\bund\b/i.test(last)) ||
      (language === 'en' && /\band\b/i.test(last)) ||
      (language === 'fr' && /\bet\b/i.test(last));

    if (embeddedConjunction) {
      var bridge = language === 'fr' ? ' ainsi que ' : language === 'en' ? ' as well as ' : ' sowie ';
      return clean.slice(0, -1).join(', ') + bridge + last;
    }

    try {
      return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(clean);
    } catch (error) {
      var conjunction = language === 'fr' ? ' et ' : language === 'en' ? ' and ' : ' und ';
      return clean.slice(0, -1).join(', ') + conjunction + last;
    }
  }

  function prefersReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function cloneSerializable(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      return null;
    }
  }

  function currentLocationKey() {
    return window.location.pathname + window.location.search;
  }

  function copyHistoryState() {
    if (!window.history) return null;
    var state = window.history.state;
    if (state === null || state === undefined) return {};
    if (Array.isArray(state)) return state.slice();
    if (typeof state !== 'object') return null;

    var copy = {};
    Object.keys(state).forEach(function (key) {
      copy[key] = state[key];
    });
    return copy;
  }

  function buildRoute(answers) {
    return QUESTION_DEFINITIONS.filter(function (definition) {
      return typeof definition.when !== 'function' || definition.when(answers);
    });
  }

  function getQuestionCopy(copy, id) {
    return copy.questions && copy.questions[id] ? copy.questions[id] : { title: id, context: '', options: {} };
  }

  function optionLabel(copy, questionId, optionId) {
    var question = getQuestionCopy(copy, questionId);
    var option = question.options && question.options[optionId];
    return Array.isArray(option) ? option[0] : String(option || optionId || '');
  }

  function summaryLabel(copy, questionId, optionId) {
    var summaryGroup = questionId;
    if (questionId === 'currentRoutine') summaryGroup = 'routine';
    if (questionId === 'recommendationSize') summaryGroup = 'size';
    var group = copy.summaries && copy.summaries[summaryGroup];
    return group && group[optionId] ? group[optionId] : optionLabel(copy, questionId, optionId);
  }

  function localizedPhrases(locale) {
    if (locale === 'en') {
      return {
        naturalMakeup: 'natural and easy to control',
        definedMakeup: 'focused without feeling overdone',
        expressionGoals: {
          lips: 'to emphasise your lips',
          lashes: 'to define your lashes',
          both: 'to emphasise both lips and lashes',
          subtle: 'to add only a very subtle accent'
        },
        purposeLead: 'The clear role here: ',
        variantSeparator: ': ',
        productFallbackType: 'ANTLITZWERK creation',
        eyeNeeds: {
          tired: 'a frequently tired appearance',
          puffy: 'puffiness, especially in the morning',
          dry: 'dryness or sensitivity',
          refresh: 'a wish for an occasional refresh',
          none: 'no particular concern'
        },
        hairNeeds: {
          cleanse: 'gentle cleansing',
          volume: 'more lightness and volume',
          dryLengths: 'care for dry lengths',
          frizz: 'less frizz and smoother ends',
          complete: 'cleansing and conditioning together'
        },
        bodyNeeds: {
          cleanse: 'gentle cleansing',
          dry: 'more moisture',
          rough: 'care for rough areas',
          glow: 'more suppleness and natural glow'
        }
      };
    }
    if (locale === 'fr') {
      return {
        naturalMakeup: 'naturelle et facile à ajuster',
        definedMakeup: 'précise sans paraître chargée',
        expressionGoals: {
          lips: 'mettre surtout vos lèvres en valeur',
          lashes: 'définir surtout vos cils',
          both: 'mettre en valeur les lèvres et les cils',
          subtle: 'ajouter seulement un accent très discret'
        },
        purposeLead: 'La fonction précise ici : ',
        variantSeparator: ' : ',
        productFallbackType: 'Œuvre ANTLITZWERK',
        eyeNeeds: {
          tired: 'un regard souvent fatigué',
          puffy: 'des gonflements, surtout le matin',
          dry: 'de la sécheresse ou de la sensibilité',
          refresh: 'le souhait de le rafraîchir ponctuellement',
          none: 'aucune préoccupation particulière'
        },
        hairNeeds: {
          cleanse: 'un nettoyage doux',
          volume: 'davantage de légèreté et de volume',
          dryLengths: 'un soin pour les longueurs sèches',
          frizz: 'moins de frisottis et des pointes plus lisses',
          complete: 'un nettoyage et un soin complémentaires'
        },
        bodyNeeds: {
          cleanse: 'un nettoyage doux',
          dry: 'davantage d’hydratation',
          rough: 'un soin pour les zones rugueuses',
          glow: 'davantage de souplesse et d’éclat naturel'
        }
      };
    }
    return {
      naturalMakeup: 'natürlich und leicht dosierbar bleiben',
      definedMakeup: 'gezielt, aber nicht überladen wirken',
      expressionGoals: {
        lips: 'vor allem deine Lippen betonen',
        lashes: 'vor allem deine Wimpern betonen',
        both: 'Lippen und Wimpern betonen',
        subtle: 'nur einen sehr dezenten Akzent setzen'
      },
      purposeLead: 'Die klare Aufgabe dabei: ',
      variantSeparator: ': ',
      productFallbackType: 'ANTLITZWERK Werk',
      eyeNeeds: {
        tired: 'einen häufig müden Ausdruck',
        puffy: 'häufige Schwellungen',
        dry: 'Trockenheit oder Empfindlichkeit',
        refresh: 'den Wunsch nach gelegentlicher Erfrischung',
        none: 'kein bestimmtes Anliegen'
      },
      hairNeeds: {
        cleanse: 'eine sanfte Reinigung',
        volume: 'mehr Leichtigkeit und Volumen',
        dryLengths: 'Pflege für trockene Längen',
        frizz: 'weniger Frizz und ruhigere Spitzen',
        complete: 'eine abgestimmte Reinigung und Pflege'
      },
      bodyNeeds: {
        cleanse: 'eine sanfte Reinigung',
        dry: 'mehr Feuchtigkeit',
        rough: 'Pflege für raue Stellen',
        glow: 'mehr Geschmeidigkeit und natürlichen Glanz'
      }
    };
  }

  function Ritual(root) {
    this.root = root;
    this.localeKey = normalizeLocale(root.dataset.awrLocale);
    this.dictionary = window.AntlitwerkRitualI18n || {};
    this.copy = this.dictionary[this.localeKey] || this.dictionary.de;
    if (!this.copy) return;

    this.locale = this.copy.locale || this.localeKey;
    this.phrases = localizedPhrases(this.localeKey);
    this.products = this.readProducts();
    this.answers = {};
    this.route = buildRoute(this.answers);
    this.currentId = this.route.length ? this.route[0].id : null;
    this.name = '';
    this.autoTimer = null;
    this.transitionTimer = null;
    this.renderToken = 0;
    this.selectionLocked = false;
    this.signature = '';
    this.returnNavigationPending = false;
    this.ignoreNextPageShow = false;
    this.boundDocumentClick = null;
    this.boundPageHide = null;
    this.boundPageShow = null;
    this.boundSectionUnload = null;

    this.refs = {
      intro: root.querySelector('[data-awr-intro]'),
      flow: root.querySelector('[data-awr-flow]'),
      results: root.querySelector('[data-awr-results]'),
      name: root.querySelector('[data-awr-name]'),
      start: root.querySelector('[data-awr-start]'),
      form: root.querySelector('[data-awr-form]'),
      stage: root.querySelector('[data-awr-stage]'),
      back: root.querySelector('[data-awr-back]'),
      note: root.querySelector('[data-awr-note]'),
      multi: root.querySelector('[data-awr-multi]'),
      multiCount: root.querySelector('[data-awr-multi-count]'),
      multiNext: root.querySelector('[data-awr-multi-next]'),
      chapter: root.querySelector('[data-awr-chapter]'),
      counter: root.querySelector('[data-awr-counter]'),
      progress: root.querySelector('[data-awr-progress]'),
      guidance: root.querySelector('[data-awr-guidance]'),
      status: root.querySelector('[data-awr-status]'),
      edit: root.querySelector('[data-awr-edit]'),
      reset: root.querySelector('[data-awr-reset]')
    };

    if (!this.refs.intro || !this.refs.flow || !this.refs.results || !this.refs.stage) return;
    this.bind();
    var restored = this.restoreRecommendationReturn({ restoreScroll: true });
    this.ignoreNextPageShow = restored && document.readyState !== 'complete';
    this.root.dataset.awrReady = 'true';
  }

  Ritual.prototype.readProducts = function () {
    var source = this.root.querySelector('[data-awr-products-json]');
    if (!source) return {};
    try {
      var products = JSON.parse(source.textContent || '{}');
      Object.keys(products).forEach(function (key) {
        var product = products[key] || {};
        ['name', 'shortName', 'type', 'price'].forEach(function (field) {
          if (product[field] !== undefined && product[field] !== null) {
            product[field] = decodeHtmlEntities(product[field]);
          }
        });
        if (!product.url && product.handle) product.url = storefrontRoot() + 'products/' + product.handle;
      });
      return products;
    } catch (error) {
      return {};
    }
  };

  Ritual.prototype.bind = function () {
    var self = this;

    if (this.refs.start) {
      this.refs.start.addEventListener('click', function () { self.start(); });
    }

    if (this.refs.name) {
      this.refs.name.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          self.start();
        }
      });
    }

    if (this.refs.form) {
      this.refs.form.addEventListener('submit', function (event) { event.preventDefault(); });
    }

    this.refs.stage.addEventListener('click', function (event) {
      var option = event.target.closest('[data-awr-option]');
      if (!option || !self.refs.stage.contains(option)) return;
      self.selectOption(option.dataset.awrOption);
    });

    if (this.refs.back) {
      this.refs.back.addEventListener('click', function () { self.goBack(); });
    }

    if (this.refs.multiNext) {
      this.refs.multiNext.addEventListener('click', function () { self.confirmMulti(); });
    }

    if (this.refs.edit) {
      this.refs.edit.addEventListener('click', function () { self.editAnswers(); });
    }

    if (this.refs.reset) {
      this.refs.reset.addEventListener('click', function () { self.reset(); });
    }

    this.boundDocumentClick = function (event) { self.handleDocumentClick(event); };
    this.boundPageHide = function () { self.handlePageHide(); };
    this.boundPageShow = function (event) { self.handlePageShow(event); };
    this.boundSectionUnload = function (event) {
      var target = event.target;
      if (target === self.root || (target && target.contains && target.contains(self.root))) self.destroy();
    };

    document.addEventListener('click', this.boundDocumentClick, true);
    window.addEventListener('pagehide', this.boundPageHide);
    window.addEventListener('pageshow', this.boundPageShow);
    document.addEventListener('shopify:section:unload', this.boundSectionUnload);
  };

  Ritual.prototype.destroy = function () {
    this.clearTimers();
    if (this.boundDocumentClick) document.removeEventListener('click', this.boundDocumentClick, true);
    if (this.boundPageHide) window.removeEventListener('pagehide', this.boundPageHide);
    if (this.boundPageShow) window.removeEventListener('pageshow', this.boundPageShow);
    if (this.boundSectionUnload) document.removeEventListener('shopify:section:unload', this.boundSectionUnload);
    this.boundDocumentClick = null;
    this.boundPageHide = null;
    this.boundPageShow = null;
    this.boundSectionUnload = null;
    delete this.root.dataset.awrReady;
  };

  Ritual.prototype.replaceHistoryState = function (state) {
    if (!window.history || !window.history.replaceState) return false;
    try {
      window.history.replaceState(state, document.title, window.location.href);
      return true;
    } catch (error) {
      return false;
    }
  };

  Ritual.prototype.readRecommendationReturn = function () {
    var state = window.history && window.history.state;
    if (!state || typeof state !== 'object') return null;
    return state[RETURN_HISTORY_KEY] || null;
  };

  Ritual.prototype.clearRecommendationReturn = function () {
    this.returnNavigationPending = false;

    var state = window.history && window.history.state;
    if (!state || typeof state !== 'object' || !Object.prototype.hasOwnProperty.call(state, RETURN_HISTORY_KEY)) return;

    var nextState = copyHistoryState();
    if (!nextState) return;
    delete nextState[RETURN_HISTORY_KEY];
    this.replaceHistoryState(nextState);
  };

  Ritual.prototype.isRecommendationProductActivation = function (event, link) {
    if (!link || !this.refs.results.contains(link)) return false;
    if (!link.classList.contains('awr-product__card-link')) return false;
    if (this.root.dataset.awrView !== 'results') return false;
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (typeof event.button === 'number' && event.button !== 0) return false;
    if (link.hasAttribute('download')) return false;
    if (link.target && link.target.toLowerCase() !== '_self') return false;

    try {
      var url = new URL(link.href, window.location.href);
      return url.origin === window.location.origin && /(^|\/)products\/[^/]+/.test(url.pathname);
    } catch (error) {
      return false;
    }
  };

  Ritual.prototype.hasCompletedResultAnswers = function (answers) {
    if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return false;
    var required = ['focus', 'dailyTime', 'stress', 'currentRoutine', 'recommendationSize', 'avoid', 'priority'];
    var complete = required.every(function (key) {
      return Object.prototype.hasOwnProperty.call(answers, key);
    });
    return complete && toArray(answers.focus).length > 0 && toArray(answers.avoid).length > 0;
  };

  Ritual.prototype.armRecommendationReturn = function (link) {
    if (!this.hasCompletedResultAnswers(this.answers)) return false;

    var nextState = copyHistoryState();
    var answers = cloneSerializable(this.answers);
    if (!nextState || !answers) return false;

    var targetUrl;
    try {
      targetUrl = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }

    nextState[RETURN_HISTORY_KEY] = {
      version: RETURN_HISTORY_VERSION,
      sectionId: String(this.root.dataset.awrSectionId || ''),
      locale: this.localeKey,
      sourceLocation: currentLocationKey(),
      targetLocation: targetUrl.pathname + targetUrl.search,
      snapshot: {
        answers: answers,
        name: firstName(this.refs.name ? this.refs.name.value : this.name),
        currentId: this.currentId,
        scrollY: Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0))
      }
    };

    if (!this.replaceHistoryState(nextState)) return false;

    this.returnNavigationPending = true;
    return true;
  };

  Ritual.prototype.handleDocumentClick = function (event) {
    var target = event.target;
    var link = target && target.closest ? target.closest('a[href]') : null;

    if (link && this.isRecommendationProductActivation(event, link)) {
      this.armRecommendationReturn(link);
      return;
    }

    if (this.returnNavigationPending) this.clearRecommendationReturn();
  };

  Ritual.prototype.isValidRecommendationReturn = function (marker) {
    if (!marker || marker.version !== RETURN_HISTORY_VERSION) return false;
    if (String(marker.sectionId || '') !== String(this.root.dataset.awrSectionId || '')) return false;
    if (marker.locale !== this.localeKey || marker.sourceLocation !== currentLocationKey()) return false;
    if (!/(^|\/)products\/[^/]+/.test(String(marker.targetLocation || '').split('?')[0])) return false;
    if (!marker.snapshot || !this.hasCompletedResultAnswers(marker.snapshot.answers)) return false;
    return true;
  };

  Ritual.prototype.restoreScrollPosition = function (value) {
    var top = Number(value);
    if (!Number.isFinite(top) || top < 0) return;

    var apply = function () {
      try {
        window.scrollTo({ top: top, left: 0, behavior: 'auto' });
      } catch (error) {
        window.scrollTo(0, top);
      }
    };

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(apply);
    });
    window.setTimeout(apply, 120);
  };

  Ritual.prototype.restoreRecommendationReturn = function (options) {
    options = options || {};
    var marker = this.readRecommendationReturn();
    if (!marker) return false;
    if (!this.isValidRecommendationReturn(marker)) {
      this.clearRecommendationReturn();
      return false;
    }

    var answers = cloneSerializable(marker.snapshot.answers);
    if (!answers) {
      this.clearRecommendationReturn();
      return false;
    }

    this.clearTimers();
    this.answers = answers;
    this.name = firstName(marker.snapshot.name);
    this.route = buildRoute(this.answers);
    this.currentId = marker.snapshot.currentId;
    this.rebuildRoute();
    if (!this.currentId || this.route.every(function (definition) { return definition.id !== this.currentId; }, this)) {
      this.currentId = this.route.length ? this.route[this.route.length - 1].id : null;
    }
    if (this.refs.name) this.refs.name.value = this.name;
    this.selectionLocked = false;
    this.signature = '';
    this.renderResults();
    this.showView('results');

    var scrollY = marker.snapshot.scrollY;
    this.clearRecommendationReturn();
    if (options.restoreScroll !== false) this.restoreScrollPosition(scrollY);
    return true;
  };

  Ritual.prototype.handlePageHide = function () {
    if (!this.returnNavigationPending) this.clearRecommendationReturn();
  };

  Ritual.prototype.handlePageShow = function (event) {
    if (this.ignoreNextPageShow) {
      this.ignoreNextPageShow = false;
      return;
    }

    if (this.restoreRecommendationReturn({ restoreScroll: !(event && event.persisted) })) return;

    if (event && event.persisted && this.returnNavigationPending && this.root.dataset.awrView === 'results') {
      this.clearRecommendationReturn();
      return;
    }

    if (event && event.persisted && this.root.dataset.awrView !== 'intro') this.freshStart();
  };

  Ritual.prototype.clearTimers = function () {
    if (this.autoTimer !== null) window.clearTimeout(this.autoTimer);
    if (this.transitionTimer !== null) window.clearTimeout(this.transitionTimer);
    this.autoTimer = null;
    this.transitionTimer = null;
  };

  Ritual.prototype.announce = function (message) {
    if (!this.refs.status) return;
    this.refs.status.textContent = '';
    var status = this.refs.status;
    window.setTimeout(function () { status.textContent = message || ''; }, 10);
  };

  Ritual.prototype.start = function () {
    this.clearTimers();
    this.clearRecommendationReturn();
    this.name = firstName(this.refs.name ? this.refs.name.value : this.name);
    this.rebuildRoute();
    if (!this.currentId || !DEFINITION_MAP[this.currentId]) this.currentId = this.route[0].id;
    this.showView('flow');
    this.renderCurrent({ instant: true, focus: true });
    this.announce(this.copy.ui.started);
    this.scrollToView(this.refs.flow, true);
  };

  Ritual.prototype.showView = function (name) {
    this.refs.intro.hidden = name !== 'intro';
    this.refs.flow.hidden = name !== 'flow';
    this.refs.results.hidden = name !== 'results';
    this.root.dataset.awrView = name;
  };

  Ritual.prototype.rebuildRoute = function () {
    var previousIds = this.route.map(function (item) { return item.id; });
    this.route = buildRoute(this.answers);
    var valid = this.route.map(function (item) { return item.id; });
    var self = this;

    Object.keys(this.answers).forEach(function (id) {
      if (valid.indexOf(id) === -1) delete self.answers[id];
    });

    if (this.currentId && valid.indexOf(this.currentId) === -1) {
      var formerIndex = Math.max(0, previousIds.indexOf(this.currentId));
      this.currentId = valid[Math.min(formerIndex, valid.length - 1)] || valid[0] || null;
    }
  };

  Ritual.prototype.currentDefinition = function () {
    return DEFINITION_MAP[this.currentId] || null;
  };

  Ritual.prototype.currentIndex = function () {
    var id = this.currentId;
    return Math.max(0, this.route.findIndex(function (definition) { return definition.id === id; }));
  };

  Ritual.prototype.renderCurrent = function (options) {
    options = options || {};
    var definition = this.currentDefinition();
    if (!definition) return;

    var self = this;
    var token = ++this.renderToken;
    var render = function () {
      if (token !== self.renderToken) return;
      self.selectionLocked = false;
      self.refs.stage.innerHTML = self.questionMarkup(definition);
      self.updateProgress(definition);
      self.updateNavigation(definition);
      self.refs.stage.classList.remove('is-leaving');
      self.refs.stage.classList.add('is-entering');

      window.requestAnimationFrame(function () {
        var title = self.refs.stage.querySelector('.awr-question__title');
        if (options.focus !== false && title) title.focus({ preventScroll: true });
      });

      window.setTimeout(function () {
        self.refs.stage.classList.remove('is-entering');
      }, prefersReducedMotion() ? 20 : STAGE_IN_MS);

      if (!options.instant) self.scrollQuestionIntoView();
    };

    if (options.instant || prefersReducedMotion() || !this.refs.stage.firstElementChild) {
      render();
      return;
    }

    this.refs.stage.classList.remove('is-entering');
    this.refs.stage.classList.add('is-leaving');
    this.transitionTimer = window.setTimeout(render, STAGE_OUT_MS);
  };

  Ritual.prototype.questionMarkup = function (definition) {
    var question = getQuestionCopy(this.copy, definition.id);
    var selected = toArray(this.answers[definition.id]);
    var options = Object.keys(question.options || {});
    var sectionId = this.root.dataset.awrSectionId || 'ritual';
    var titleId = 'AwrQuestion-' + sectionId + '-' + definition.id;
    var optionClass = 'awr-options';
    if (definition.layout === 'three') optionClass += ' awr-options--three';
    if (definition.layout === 'scale') optionClass += ' awr-options--scale';

    var buttons = options.map(function (value, index) {
      var option = question.options[value];
      var label = Array.isArray(option) ? option[0] : option;
      var hint = Array.isArray(option) ? option[1] : '';
      var isSelected = selected.indexOf(value) !== -1;
      var classes = 'awr-option';
      if (definition.layout === 'scale') classes += ' awr-option--scale';
      if (isSelected) classes += ' is-selected';
      var scaleIndex = definition.layout === 'scale' ? ' data-scale-index="' + pad(index + 1) + '"' : '';

      return '<button type="button" class="' + classes + '" data-awr-option="' + escapeAttribute(value) + '"' +
        scaleIndex + ' aria-pressed="' + (isSelected ? 'true' : 'false') + '">' +
        '<span class="awr-option__copy"' + scaleIndex + '>' +
          '<span class="awr-option__label">' + escapeHtml(label) + '</span>' +
          (hint ? '<span class="awr-option__hint">' + escapeHtml(hint) + '</span>' : '') +
        '</span>' +
      '</button>';
    }).join('');

    var questionNumber = replaceTokens(this.copy.ui.question, { current: this.currentIndex() + 1 });
    return '<section class="awr-question" data-awr-question="' + escapeAttribute(definition.id) + '">' +
      '<p class="awr-question__number">' + escapeHtml(questionNumber) + '</p>' +
      '<h2 id="' + escapeAttribute(titleId) + '" class="awr-question__title" tabindex="-1">' + escapeHtml(question.title) + '</h2>' +
      (question.context ? '<p class="awr-question__context">' + escapeHtml(question.context) + '</p>' : '') +
      '<div class="' + optionClass + '" role="group" aria-labelledby="' + escapeAttribute(titleId) + '">' + buttons + '</div>' +
    '</section>';
  };

  Ritual.prototype.updateProgress = function (definition) {
    var index = this.currentIndex();
    var total = this.route.length;
    var position = total > 1 ? (index / (total - 1)) * 100 : 100;

    if (this.refs.chapter) this.refs.chapter.textContent = this.copy.chapters[definition.chapter] || '';
    if (this.refs.counter) this.refs.counter.textContent = pad(index + 1);
    if (this.refs.guidance) this.refs.guidance.textContent = this.copy.ui.guidance[definition.chapter] || '';
    if (this.refs.progress) {
      this.refs.progress.setAttribute('aria-valuemax', String(total));
      this.refs.progress.setAttribute('aria-valuenow', String(index + 1));
      this.refs.progress.style.setProperty('--awr-progress-position', position + '%');
    }
  };

  Ritual.prototype.updateNavigation = function (definition) {
    var isMulti = definition.type === 'multi';
    if (this.refs.note) this.refs.note.hidden = isMulti;
    if (this.refs.multi) this.refs.multi.hidden = !isMulti;
    if (isMulti) this.updateMultiState(definition);
  };

  Ritual.prototype.updateMultiState = function (definition) {
    var count = toArray(this.answers[definition.id]).length;
    var max = definition.max || 99;
    if (this.refs.multiCount) {
      this.refs.multiCount.textContent = replaceTokens(this.copy.ui.selected, { count: count, max: max });
    }
    if (this.refs.multiNext) this.refs.multiNext.disabled = count === 0;
  };

  Ritual.prototype.selectOption = function (value) {
    var definition = this.currentDefinition();
    if (!definition || !value || this.selectionLocked) return;
    this.clearTimers();

    if (definition.type === 'multi') {
      this.toggleMulti(definition, value);
      return;
    }

    this.selectionLocked = true;
    this.answers[definition.id] = value;
    this.reflectSelection(value, false);
    this.rebuildRoute();
    this.announce(this.copy.ui.confirmed);

    var self = this;
    this.autoTimer = window.setTimeout(function () {
      self.advance();
    }, prefersReducedMotion() ? 30 : AUTO_ADVANCE_MS);
  };

  Ritual.prototype.toggleMulti = function (definition, value) {
    var selected = toArray(this.answers[definition.id]);
    var index = selected.indexOf(value);

    if (definition.id === 'avoid') {
      if (value === 'none') {
        selected = index === -1 ? ['none'] : [];
      } else {
        selected = selected.filter(function (entry) { return entry !== 'none'; });
        index = selected.indexOf(value);
        if (index === -1) {
          if (selected.length >= definition.max) {
            this.announce(replaceTokens(this.copy.ui.limitReached, { max: definition.max }));
            return;
          }
          selected.push(value);
        } else {
          selected.splice(index, 1);
        }
      }
    } else if (index === -1) {
      if (selected.length >= definition.max) {
        this.announce(replaceTokens(this.copy.ui.limitReached, { max: definition.max }));
        return;
      }
      selected.push(value);
    } else {
      selected.splice(index, 1);
    }

    this.answers[definition.id] = selected;
    this.reflectMultiSelections(selected);
    this.updateMultiState(definition);
  };

  Ritual.prototype.reflectSelection = function (value, multi) {
    var buttons = this.refs.stage.querySelectorAll('[data-awr-option]');
    Array.prototype.forEach.call(buttons, function (button) {
      var selected = multi ? false : button.dataset.awrOption === value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  };

  Ritual.prototype.reflectMultiSelections = function (selected) {
    var buttons = this.refs.stage.querySelectorAll('[data-awr-option]');
    Array.prototype.forEach.call(buttons, function (button) {
      var isSelected = selected.indexOf(button.dataset.awrOption) !== -1;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  };

  Ritual.prototype.confirmMulti = function () {
    var definition = this.currentDefinition();
    if (!definition || definition.type !== 'multi') return;
    var selected = toArray(this.answers[definition.id]);
    if (!selected.length) {
      this.announce(this.copy.ui.selectOne);
      return;
    }
    this.rebuildRoute();
    this.announce(this.copy.ui.confirmed);
    this.advance();
  };

  Ritual.prototype.advance = function () {
    this.clearTimers();
    this.rebuildRoute();
    var index = this.currentIndex();
    if (index >= this.route.length - 1) {
      this.prepareResults();
      return;
    }
    this.currentId = this.route[index + 1].id;
    this.renderCurrent({ focus: true });
  };

  Ritual.prototype.goBack = function () {
    this.clearTimers();
    this.rebuildRoute();
    var index = this.currentIndex();
    if (index <= 0) {
      this.showView('intro');
      this.announce(this.copy.ui.backAtStart);
      this.scrollToView(this.refs.intro, true);
      if (this.refs.name) this.refs.name.focus({ preventScroll: true });
      return;
    }
    this.currentId = this.route[index - 1].id;
    this.renderCurrent({ focus: true });
  };

  Ritual.prototype.prepareResults = function () {
    var self = this;
    this.clearTimers();
    this.announce(this.copy.ui.preparingResults);
    if (this.refs.multiNext) this.refs.multiNext.disabled = true;
    this.autoTimer = window.setTimeout(function () {
      self.renderResults();
      self.showView('results');
      self.announce(self.copy.ui.resultsReady);
      self.scrollToView(self.refs.results, true);
      var resultTitle = self.root.querySelector('[data-awr-result-title]');
      if (resultTitle) resultTitle.focus({ preventScroll: true });
    }, prefersReducedMotion() ? 40 : 360);
  };

  Ritual.prototype.editAnswers = function () {
    this.clearTimers();
    this.clearRecommendationReturn();
    this.rebuildRoute();
    this.currentId = this.route.length ? this.route[this.route.length - 1].id : null;
    this.showView('flow');
    this.renderCurrent({ instant: true, focus: true });
    this.announce(this.copy.ui.editReady);
    this.scrollToView(this.refs.flow, true);
  };

  Ritual.prototype.freshStart = function () {
    this.clearTimers();
    this.clearRecommendationReturn();
    this.answers = {};
    this.route = buildRoute(this.answers);
    this.currentId = this.route.length ? this.route[0].id : null;
    this.name = '';
    this.signature = '';
    this.selectionLocked = false;
    if (this.refs.name) this.refs.name.value = '';
    if (this.refs.stage) this.refs.stage.innerHTML = '';

    var products = this.root.querySelector('[data-awr-products]');
    var signals = this.root.querySelector('[data-awr-signals]');
    if (products) {
      products.innerHTML = '';
      products.classList.remove('awr-products--empty');
    }
    if (signals) signals.innerHTML = '';

    this.showView('intro');
  };

  Ritual.prototype.reset = function () {
    this.freshStart();
    this.announce(this.copy.ui.resetDone);
    this.scrollToView(this.refs.intro, true);
    if (this.refs.name) this.refs.name.focus({ preventScroll: true });
  };

  Ritual.prototype.scrollToView = function (element, force) {
    if (!element) return;
    var rect = element.getBoundingClientRect();
    var top = rect.top + window.scrollY - 92;
    if (!force && rect.top > 80 && rect.top < window.innerHeight * 0.45) return;
    window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  Ritual.prototype.scrollQuestionIntoView = function () {
    var flow = this.refs.flow;
    if (!flow) return;
    var rect = flow.getBoundingClientRect();
    if (rect.top < -24 || rect.top > window.innerHeight * 0.25) this.scrollToView(flow, false);
  };

  Ritual.prototype.scoreProducts = function () {
    var answers = this.answers;
    var focus = toArray(answers.focus);
    var avoid = toArray(answers.avoid);
    var faceGoals = toArray(answers.faceGoals);
    var hairNeed = toArray(answers.hairNeed);
    var bodyNeed = toArray(answers.bodyNeed);
    var scores = {};
    var evidence = {};

    PRODUCT_KEYS.forEach(function (key) {
      scores[key] = 0;
      evidence[key] = [];
    });

    function add(key, points, reason) {
      scores[key] += points;
      if (reason && evidence[key].indexOf(reason) === -1) evidence[key].push(reason);
    }

    if (focus.indexOf('face') !== -1) {
      add('zeitanmut', 2, 'face');
      add('tagesnacht', 2, 'face');
      add('lichtessenz', 1, 'face');
    }
    if (focus.indexOf('eyes') !== -1) add('augenlicht', 3, 'eyes');
    if (focus.indexOf('expression') !== -1) {
      add('rosenanmut', 3, 'expression');
      add('wimpernwerk', 3, 'expression');
    }
    if (focus.indexOf('hair') !== -1) {
      add('seidenthron', 3, 'hair');
      add('seidenkrone', 3, 'hair');
    }
    if (focus.indexOf('body') !== -1) {
      add('seelenbad', 2, 'body');
      add('hautfrieden', 2, 'body');
      add('koerperlicht', 2, 'body');
    }
    if (focus.indexOf('calm') !== -1) {
      add('seelenflamme', 4, 'calm');
      add('seelenbad', 1, 'calm');
    }

    switch (answers.faceFeel) {
      case 'dry':
        add('zeitanmut', 5, 'dry'); add('tagesnacht', 7, 'dry'); add('lichtessenz', 5, 'dry'); break;
      case 'balanced':
        add('zeitanmut', 2, 'balanced'); add('tagesnacht', 3, 'balanced'); add('lichtessenz', 2, 'balanced'); break;
      case 'oily':
        add('zeitanmut', 7, 'oily'); add('tagesnacht', 2, 'oily'); add('lichtessenz', -5, 'oily'); break;
      case 'combination':
        add('zeitanmut', 6, 'combination'); add('tagesnacht', 3, 'combination'); add('lichtessenz', -2, 'combination'); break;
      case 'sensitive':
        add('zeitanmut', 5, 'sensitive'); add('tagesnacht', 4, 'sensitive'); add('enthuellung', 2, 'sensitive'); break;
      case 'unsure':
        add('zeitanmut', 3, 'unsure'); add('tagesnacht', 3, 'unsure'); break;
    }

    faceGoals.forEach(function (goal) {
      if (goal === 'hydration') {
        add('zeitanmut', 8, goal); add('tagesnacht', 3, goal); add('lichtessenz', 2, goal);
      } else if (goal === 'firmness') {
        add('tagesnacht', 8, goal); add('lichtessenz', 3, goal); add('zeitanmut', 2, goal);
      } else if (goal === 'glow') {
        add('lichtessenz', 7, goal); add('zeitanmut', 3, goal); add('tagesnacht', 2, goal);
      } else if (goal === 'dailyComfort') {
        add('tagesnacht', 8, goal); add('zeitanmut', 3, goal);
      } else if (goal === 'removal') {
        add('enthuellung', 11, goal);
      }
    });

    if (answers.faceTexture === 'light') {
      add('zeitanmut', 5, 'light'); add('tagesnacht', -1, 'light'); add('lichtessenz', -6, 'light');
    } else if (answers.faceTexture === 'cream') {
      add('tagesnacht', 5, 'cream');
    } else if (answers.faceTexture === 'oil') {
      add('lichtessenz', 7, 'oil');
    }

    if (answers.eyeNeed === 'tired') add('augenlicht', 10, 'tired');
    if (answers.eyeNeed === 'puffy') add('augenlicht', 11, 'puffy');
    if (answers.eyeNeed === 'dry') add('augenlicht', 8, 'dry');
    if (answers.eyeNeed === 'refresh') add('augenlicht', 7, 'refresh');
    if (answers.eyeNeed === 'none') add('augenlicht', 4, 'none');
    if (answers.morning === 'aBitTired') add('augenlicht', 1, 'morning');
    if (answers.morning === 'oftenTired') add('augenlicht', 3, 'morning');
    if (answers.morning === 'exhausted') add('augenlicht', 5, 'morning');

    if (answers.expressionGoal === 'lips') {
      add('rosenanmut', 11, 'lips'); add('wimpernwerk', 1, 'lips');
    } else if (answers.expressionGoal === 'lashes') {
      add('wimpernwerk', 11, 'lashes');
    } else if (answers.expressionGoal === 'both') {
      add('rosenanmut', 9, 'both'); add('wimpernwerk', 9, 'both');
    } else if (answers.expressionGoal === 'subtle') {
      add('rosenanmut', 5, 'subtle'); add('wimpernwerk', 6, 'subtle');
    }

    if (answers.makeupUse === 'rarely') {
      add('rosenanmut', -1, 'rarely'); add('wimpernwerk', -1, 'rarely');
    } else if (answers.makeupUse === 'sometimes') {
      add('rosenanmut', 2, 'sometimes'); add('wimpernwerk', 2, 'sometimes'); add('enthuellung', 4, 'sometimes');
    } else if (answers.makeupUse === 'often') {
      add('rosenanmut', 4, 'often'); add('wimpernwerk', 4, 'often'); add('enthuellung', 8, 'often');
    }
    if (answers.lipShade) add('rosenanmut', 2, 'shade');

    hairNeed.forEach(function (need) {
      if (need === 'cleanse') add('seidenthron', 10, need);
      if (need === 'volume') { add('seidenthron', 10, need); add('seidenkrone', -2, need); }
      if (need === 'dryLengths') { add('seidenkrone', 11, need); add('seidenthron', 2, need); }
      if (need === 'frizz') add('seidenkrone', 10, need);
      if (need === 'complete') { add('seidenthron', 8, need); add('seidenkrone', 8, need); }
    });

    bodyNeed.forEach(function (need) {
      if (need === 'cleanse') add('seelenbad', 11, need);
      if (need === 'dry') { add('hautfrieden', 8, need); add('koerperlicht', 6, need); add('seelenbad', 2, need); }
      if (need === 'rough') { add('hautfrieden', 11, need); add('koerperlicht', 3, need); }
      if (need === 'glow') { add('koerperlicht', 10, need); add('hautfrieden', 2, need); }
    });

    if (answers.bodyTexture === 'cream') add('hautfrieden', 5, 'cream');
    if (answers.bodyTexture === 'oil') add('koerperlicht', 5, 'oil');
    if (answers.bodyTexture === 'both') { add('hautfrieden', 3, 'both'); add('koerperlicht', 3, 'both'); }

    if (answers.eveningNeed && answers.eveningNeed !== 'noFragrance') {
      add('seelenflamme', 9, answers.eveningNeed);
      add('seelenbad', 2, answers.eveningNeed);
    }
    if (answers.stress === 'often') { add('seelenflamme', 2, 'stress'); add('seelenbad', 1, 'stress'); }
    if (answers.stress === 'very') { add('seelenflamme', 4, 'stress'); add('seelenbad', 1, 'stress'); }

    if (answers.scentTolerance === 'subtle') { add('seelenflamme', -2, 'subtle-scent'); add('seelenbad', -1, 'subtle-scent'); }
    if (answers.scentTolerance === 'present') { add('seelenflamme', 2, 'present-scent'); add('seelenbad', 1, 'present-scent'); }
    if (answers.scentTolerance === 'none' || answers.eveningNeed === 'noFragrance') {
      scores.seelenflamme = -100;
      scores.seelenbad = -100;
    }

    if (answers.dailyTime === 'veryShort') {
      add('tagesnacht', 2, 'time'); add('zeitanmut', 1, 'time'); add('lichtessenz', -2, 'time');
      add('koerperlicht', -1, 'time'); add('seelenflamme', -1, 'time');
    }
    if (answers.dailyTime === 'generous') {
      add('lichtessenz', 1, 'time'); add('seidenkrone', 1, 'time'); add('koerperlicht', 1, 'time');
    }

    if (answers.priority === 'simple') {
      add('zeitanmut', 2, 'priority'); add('tagesnacht', 3, 'priority'); add('augenlicht', 1, 'priority');
    } else if (answers.priority === 'visible') {
      add('lichtessenz', 2, 'priority'); add('augenlicht', 2, 'priority'); add('rosenanmut', 2, 'priority');
      add('wimpernwerk', 2, 'priority'); add('hautfrieden', 2, 'priority'); add('koerperlicht', 2, 'priority');
    } else if (answers.priority === 'gentle') {
      add('zeitanmut', 2, 'priority'); add('tagesnacht', 2, 'priority'); add('augenlicht', 2, 'priority');
      add('enthuellung', 2, 'priority'); add('seelenbad', 2, 'priority'); add('hautfrieden', 2, 'priority');
    } else if (answers.priority === 'calm') {
      add('seelenflamme', 4, 'priority'); add('seelenbad', 2, 'priority');
    } else if (answers.priority === 'natural') {
      add('rosenanmut', 2, 'priority'); add('wimpernwerk', 2, 'priority'); add('lichtessenz', 1, 'priority');
    } else if (answers.priority === 'balance') {
      add('zeitanmut', 1, 'priority'); add('tagesnacht', 2, 'priority'); add('seidenthron', 1, 'priority'); add('seelenbad', 1, 'priority');
    }

    if (avoid.indexOf('heavy') !== -1) {
      add('zeitanmut', 2, 'avoid-heavy'); add('lichtessenz', -9, 'avoid-heavy'); add('koerperlicht', -8, 'avoid-heavy');
      add('tagesnacht', -2, 'avoid-heavy'); add('hautfrieden', -2, 'avoid-heavy');
    }
    if (avoid.indexOf('strongScent') !== -1) {
      add('seelenflamme', -10, 'avoid-scent'); add('seelenbad', -5, 'avoid-scent');
    }
    if (avoid.indexOf('visibleMakeup') !== -1) {
      add('rosenanmut', -3, 'avoid-makeup'); add('wimpernwerk', -2, 'avoid-makeup');
    }
    if (avoid.indexOf('dailyObligation') !== -1) {
      add('augenlicht', 2, 'flexible'); add('lichtessenz', 1, 'flexible'); add('rosenanmut', 1, 'flexible');
      add('seelenflamme', 1, 'flexible'); add('tagesnacht', -1, 'flexible');
    }

    return { scores: scores, evidence: evidence };
  };

  Ritual.prototype.isEligible = function (key) {
    var answers = this.answers;
    var focus = toArray(answers.focus);
    if (['zeitanmut', 'tagesnacht', 'lichtessenz'].indexOf(key) !== -1) return focus.indexOf('face') !== -1;
    if (key === 'enthuellung') {
      return contains(answers.faceGoals, 'removal') ||
        (focus.indexOf('expression') !== -1 && ['sometimes', 'often'].indexOf(answers.makeupUse) !== -1) ||
        (focus.indexOf('face') !== -1 && ['sometimes', 'often'].indexOf(answers.makeupUse) !== -1);
    }
    if (key === 'augenlicht') return focus.indexOf('eyes') !== -1;
    if (['rosenanmut', 'wimpernwerk'].indexOf(key) !== -1) return focus.indexOf('expression') !== -1;
    if (['seidenthron', 'seidenkrone'].indexOf(key) !== -1) return focus.indexOf('hair') !== -1;
    if (['hautfrieden', 'koerperlicht'].indexOf(key) !== -1) return focus.indexOf('body') !== -1;
    if (key === 'seelenbad') return focus.indexOf('body') !== -1 || focus.indexOf('calm') !== -1;
    if (key === 'seelenflamme') return focus.indexOf('calm') !== -1;
    return false;
  };

  Ritual.prototype.recommendationCap = function () {
    var answers = this.answers;
    var avoid = toArray(answers.avoid);
    var cap;

    if (answers.recommendationSize === 'one') cap = 1;
    else if (answers.recommendationSize === 'small') cap = 3;
    else if (answers.recommendationSize === 'full') cap = 5;
    else {
      cap = answers.dailyTime === 'veryShort' ? 1 :
        answers.dailyTime === 'short' ? 2 :
        answers.dailyTime === 'medium' ? 4 : 5;
    }

    if (answers.dailyTime === 'veryShort') cap = Math.min(cap, 1);
    if (answers.dailyTime === 'short') cap = Math.min(cap, 3);
    if (answers.currentRoutine === 'none') cap = Math.min(cap, answers.recommendationSize === 'full' ? 3 : 2);
    if (answers.currentRoutine === 'oneTwo') cap = Math.min(cap, 3);
    if (answers.stress === 'very') cap = Math.min(cap, 2);
    if (answers.stress === 'often') cap = Math.min(cap, 3);
    if (avoid.indexOf('manySteps') !== -1) cap = Math.min(cap, 2);
    if (avoid.indexOf('dailyObligation') !== -1) cap = Math.min(cap, 3);

    return clamp(cap || 1, 1, MAX_RECOMMENDATIONS);
  };

  Ritual.prototype.selectRecommendations = function () {
    var scoring = this.scoreProducts();
    var answers = this.answers;
    var avoid = toArray(answers.avoid);
    var cap = this.recommendationCap();
    var self = this;

    var candidates = PRODUCT_KEYS.map(function (key) {
      return {
        key: key,
        score: scoring.scores[key],
        evidence: scoring.evidence[key],
        meta: PRODUCT_META[key],
        product: self.products[key] || {}
      };
    }).filter(function (candidate) {
      var product = candidate.product;
      return self.isEligible(candidate.key) && candidate.score >= 7 && product.available !== false;
    }).sort(function (a, b) {
      return b.score - a.score || a.meta.order - b.meta.order;
    });

    if (!candidates.length) return { selected: [], candidates: [], cap: cap, scoring: scoring };

    var selected = [candidates[0]];
    var focus = toArray(answers.focus);

    function selectedDomain(domain) {
      return selected.some(function (candidate) { return candidate.meta.domain === domain; });
    }

    focus.forEach(function (domain) {
      if (selected.length >= cap || selectedDomain(domain)) return;
      var best = candidates.find(function (candidate) {
        return candidate.meta.domain === domain && selected.indexOf(candidate) === -1 && candidate.score >= 8;
      });
      if (best) selected.push(best);
    });

    candidates.forEach(function (candidate) {
      if (selected.length >= cap || selected.indexOf(candidate) !== -1) return;

      var domainCount = selected.filter(function (item) { return item.meta.domain === candidate.meta.domain; }).length;
      var domainLimit = candidate.meta.domain === 'face' && answers.recommendationSize === 'full' ? 3 : 2;
      if (candidate.meta.domain === 'calm' || candidate.meta.domain === 'eyes') domainLimit = 1;
      if (candidate.meta.domain === 'expression' && avoid.indexOf('visibleMakeup') !== -1) domainLimit = 1;
      if (domainCount >= domainLimit) return;

      var duplicateTask = selected.some(function (item) { return item.meta.task === candidate.meta.task; });
      if (duplicateTask) return;

      if (candidate.score < 8 && selected.length > 0) return;
      selected.push(candidate);
    });

    var core = candidates[0];
    var focusOrder = toArray(answers.focus);
    var remaining = selected.filter(function (candidate) { return candidate !== core; });
    remaining.sort(function (a, b) {
      var aCoreDomain = a.meta.domain === core.meta.domain ? 0 : 1;
      var bCoreDomain = b.meta.domain === core.meta.domain ? 0 : 1;
      if (aCoreDomain !== bCoreDomain) return aCoreDomain - bCoreDomain;
      var aFocus = focusOrder.indexOf(a.meta.domain);
      var bFocus = focusOrder.indexOf(b.meta.domain);
      aFocus = aFocus === -1 ? 99 : aFocus;
      bFocus = bFocus === -1 ? 99 : bFocus;
      if (aFocus !== bFocus) return aFocus - bFocus;
      return a.meta.usage - b.meta.usage || b.score - a.score;
    });
    selected = [core].concat(remaining);

    return { selected: selected.slice(0, cap), candidates: candidates, cap: cap, scoring: scoring };
  };

  Ritual.prototype.productReasonKey = function (key) {
    var answers = this.answers;
    var goals = toArray(answers.faceGoals);
    var hair = toArray(answers.hairNeed);
    var body = toArray(answers.bodyNeed);
    var avoid = toArray(answers.avoid);

    if (key === 'zeitanmut') {
      if (goals.indexOf('hydration') !== -1 && answers.faceTexture === 'light') return 'hydrationLight';
      if (['oily', 'combination'].indexOf(answers.faceFeel) !== -1) return 'balance';
      if (answers.faceFeel === 'sensitive' || answers.priority === 'gentle') return 'gentle';
      return 'hydration';
    }
    if (key === 'tagesnacht') {
      if (goals.indexOf('dailyComfort') !== -1) return 'daily';
      if (goals.indexOf('firmness') !== -1) return 'firmness';
      if (answers.faceFeel === 'dry') return 'dry';
      return 'simple';
    }
    if (key === 'augenlicht') {
      if (answers.eyeNeed === 'puffy') return 'puffy';
      if (answers.eyeNeed === 'dry') return 'dry';
      if (answers.eyeNeed === 'refresh' || answers.eyeNeed === 'none') return 'refresh';
      return 'tired';
    }
    if (key === 'lichtessenz') {
      if (goals.indexOf('glow') !== -1 && answers.faceTexture === 'oil') return 'glowOil';
      if (answers.faceFeel === 'dry' && answers.faceTexture === 'oil') return 'dryOil';
      if (goals.indexOf('glow') !== -1) return 'glow';
      return 'fallback';
    }
    if (key === 'enthuellung') {
      if (answers.makeupUse === 'often') return 'frequent';
      if (answers.faceFeel === 'sensitive') return 'sensitive';
      if (goals.indexOf('removal') !== -1 || answers.priority === 'gentle') return 'gentle';
      return 'fallback';
    }
    if (key === 'rosenanmut') {
      if (avoid.indexOf('visibleMakeup') !== -1 || answers.expressionGoal === 'subtle') return 'subtle';
      if (this.lipVariantKey() === 'terracotta') return 'terracotta';
      if (this.lipVariantKey() === 'rose') return 'rose';
      return 'expression';
    }
    if (key === 'wimpernwerk') {
      if (avoid.indexOf('visibleMakeup') !== -1 || answers.expressionGoal === 'subtle') return 'subtle';
      if (answers.expressionGoal === 'both') return 'both';
      if (answers.expressionGoal === 'lashes') return 'definition';
      return 'fallback';
    }
    if (key === 'seidenthron') {
      if (hair.indexOf('volume') !== -1) return 'volume';
      if (hair.indexOf('cleanse') !== -1) return 'cleanse';
      if (hair.indexOf('complete') !== -1) return 'complete';
      return 'fallback';
    }
    if (key === 'seidenkrone') {
      if (hair.indexOf('dryLengths') !== -1) return 'dry';
      if (hair.indexOf('frizz') !== -1) return 'frizz';
      if (hair.indexOf('complete') !== -1) return 'complete';
      return 'fallback';
    }
    if (key === 'seelenbad') {
      if (body.indexOf('cleanse') !== -1) return 'cleanse';
      if (contains(answers.focus, 'calm')) return 'calm';
      if (contains(answers.focus, 'body')) return 'body';
      return 'fallback';
    }
    if (key === 'hautfrieden') {
      if (body.indexOf('rough') !== -1) return 'rough';
      if (body.indexOf('dry') !== -1) return 'dry';
      if (answers.bodyTexture === 'cream') return 'cream';
      return 'fallback';
    }
    if (key === 'koerperlicht') {
      if (body.indexOf('glow') !== -1) return 'glow';
      if (answers.bodyTexture === 'oil') return 'oil';
      if (body.indexOf('dry') !== -1) return 'dry';
      return 'fallback';
    }
    if (key === 'seelenflamme') {
      if (['quiet', 'ground', 'fresh', 'warm'].indexOf(answers.eveningNeed) !== -1) return answers.eveningNeed;
      return 'stress';
    }
    return 'fallback';
  };

  Ritual.prototype.lipVariantKey = function () {
    if (this.answers.lipShade === 'rose' || this.answers.lipShade === 'terracotta') return this.answers.lipShade;
    if (contains(this.answers.avoid, 'visibleMakeup')) return 'rose';
    if (['ground', 'warm'].indexOf(this.answers.eveningNeed) !== -1) return 'terracotta';
    if (this.answers.priority === 'visible') return 'terracotta';
    return 'rose';
  };

  Ritual.prototype.productVariant = function (key) {
    var variants = this.copy.variants || {};
    if (key === 'rosenanmut') {
      var lipKey = this.lipVariantKey();
      return variants.lip && variants.lip[lipKey]
        ? this.copy.ui.shade + this.phrases.variantSeparator + variants.lip[lipKey]
        : '';
    }
    if (key === 'seelenflamme') {
      var candleKey = ['quiet', 'ground', 'fresh', 'warm'].indexOf(this.answers.eveningNeed) !== -1
        ? this.answers.eveningNeed : 'quiet';
      return variants.candle && variants.candle[candleKey]
        ? this.copy.ui.fragranceWorld + this.phrases.variantSeparator + variants.candle[candleKey]
        : '';
    }
    if (key === 'seelenbad') {
      var washKey = ['quiet', 'ground', 'fresh', 'warm'].indexOf(this.answers.eveningNeed) !== -1
        ? this.answers.eveningNeed
        : (this.answers.morning === 'exhausted' || this.answers.priority === 'visible' ? 'energy' : 'quiet');
      return variants.wash && variants.wash[washKey]
        ? this.copy.ui.scent + this.phrases.variantSeparator + variants.wash[washKey]
        : '';
    }
    return '';
  };

  Ritual.prototype.profileTheme = function (selection) {
    var answers = this.answers;
    var focus = toArray(answers.focus);
    var avoid = toArray(answers.avoid);
    var lowTime = answers.dailyTime === 'veryShort' || answers.recommendationSize === 'one';
    var highStress = answers.stress === 'often' || answers.stress === 'very';
    var sensitive = answers.faceFeel === 'sensitive' || answers.priority === 'gentle';
    var naturalExpression = focus.indexOf('expression') !== -1 &&
      (answers.expressionGoal === 'subtle' || avoid.indexOf('visibleMakeup') !== -1 || answers.priority === 'natural');

    if (highStress && (focus.indexOf('calm') !== -1 || lowTime)) return 'calm_relief';
    if (lowTime || avoid.indexOf('manySteps') !== -1) return 'simple_effect';
    if (sensitive && focus.indexOf('face') !== -1) return 'gentle_foundation';
    if (naturalExpression) return 'natural_expression';
    if (selection.selected.length >= 4) return 'connected_ritual';
    if (answers.currentRoutine === 'many' || (answers.currentRoutine === 'threeFour' && answers.stress === 'calm')) return 'steady_balance';
    if (['oftenTired', 'exhausted'].indexOf(answers.morning) !== -1) return 'renewed_energy';
    return 'focused_care';
  };

  Ritual.prototype.primaryDomain = function (selection) {
    if (selection.selected.length) return selection.selected[0].meta.domain;
    return toArray(this.answers.focus)[0] || 'mixed';
  };

  Ritual.prototype.answerSignature = function () {
    return stableStringify({ answers: this.answers, name: this.name });
  };

  Ritual.prototype.resultContext = function (selection) {
    var copy = this.copy;
    var answers = this.answers;
    var focus = toArray(answers.focus);
    var primaryDomain = this.primaryDomain(selection);
    var observationDomain = focus.length >= 3 ? 'mixed' : primaryDomain;
    var signature = this.answerSignature();
    var themeKey = this.profileTheme(selection);
    var theme = copy.results.themes[themeKey] || copy.results.themes.focused_care;
    var namePrefix = this.name ? this.name + ', ' : '';
    var title = replaceTokens(pick(theme.titles, signature, 'hero-title'), { name: namePrefix });
    if (!this.name) title = upperFirst(title, this.locale);

    var focusText = listFormat(focus.map(function (item) {
      return summaryLabel(copy, 'focus', item);
    }), this.locale);
    var leadTemplates = selection.selected.length
      ? copy.results.leadTemplates
      : (copy.results.zeroLeadTemplates || copy.results.leadTemplates);
    var lead = replaceTokens(pick(leadTemplates, signature, 'hero-lead'), {
      focus: focusText,
      time: summaryLabel(copy, 'dailyTime', answers.dailyTime),
      size: summaryLabel(copy, 'recommendationSize', answers.recommendationSize),
      priority: summaryLabel(copy, 'priority', answers.priority)
    });

    return {
      signature: signature,
      themeKey: themeKey,
      theme: theme,
      title: title,
      lead: lead,
      primaryDomain: primaryDomain,
      observationDomain: observationDomain
    };
  };

  Ritual.prototype.eveningPhrase = function () {
    var key = this.answers.eveningNeed;
    var phrases = {
      de: {
        quiet: 'ruhige und weiche',
        ground: 'erdige und klare',
        fresh: 'frische und leichte',
        warm: 'warme und geborgene',
        noFragrance: 'ruhige, duftfreie'
      },
      en: {
        quiet: 'quiet and soft',
        ground: 'grounded and clear',
        fresh: 'fresh and light',
        warm: 'warm and comforting',
        noFragrance: 'quiet, fragrance-free'
      },
      fr: {
        quiet: 'douce et apaisante',
        ground: 'ancrée et claire',
        fresh: 'fraîche et légère',
        warm: 'chaleureuse et réconfortante',
        noFragrance: 'calme et sans parfum'
      }
    };
    var language = phrases[this.localeKey] || phrases.de;
    return language[key] || lowerFirst(optionLabel(this.copy, 'eveningNeed', key), this.locale);
  };

  Ritual.prototype.observationText = function (context) {
    var copy = this.copy;
    var answers = this.answers;
    var domain = context.observationDomain;
    var key = domain;
    if (domain === 'face') {
      key = answers.faceFeel === 'dry' ? 'faceDry' :
        answers.faceFeel === 'sensitive' ? 'faceSensitive' :
        answers.faceFeel === 'balanced' ? 'faceBalanced' : 'faceOther';
    }

    var goals = listFormat(toArray(answers.faceGoals).map(function (item) {
      return lowerFirst(optionLabel(copy, 'faceGoals', item), copy.locale);
    }), this.locale) || lowerFirst(summaryLabel(copy, 'priority', answers.priority), this.locale);

    var values = {
      goals: goals,
      eyeNeed: this.phrases.eyeNeeds[answers.eyeNeed] || lowerFirst(optionLabel(copy, 'eyeNeed', answers.eyeNeed), this.locale),
      expression: this.phrases.expressionGoals[answers.expressionGoal] || lowerFirst(optionLabel(copy, 'expressionGoal', answers.expressionGoal), this.locale),
      makeupStyle: contains(answers.avoid, 'visibleMakeup') || answers.expressionGoal === 'subtle'
        ? this.phrases.naturalMakeup : this.phrases.definedMakeup,
      hairNeeds: listFormat(toArray(answers.hairNeed).map(function (item) {
        return this.phrases.hairNeeds[item] || lowerFirst(optionLabel(copy, 'hairNeed', item), copy.locale);
      }, this), this.locale),
      bodyNeeds: listFormat(toArray(answers.bodyNeed).map(function (item) {
        return this.phrases.bodyNeeds[item] || lowerFirst(optionLabel(copy, 'bodyNeed', item), copy.locale);
      }, this), this.locale),
      evening: this.eveningPhrase(),
      focus: listFormat(toArray(answers.focus).map(function (item) {
        return summaryLabel(copy, 'focus', item);
      }), this.locale)
    };

    var main = replaceTokens(pick(copy.results.observations[key] || copy.results.observations.mixed, context.signature, 'observation'), values);
    var everydayKey = (answers.dailyTime === 'veryShort' || answers.recommendationSize === 'one') ? 'lowTime' :
      (answers.stress === 'often' || answers.stress === 'very') ? 'highStress' :
      (answers.currentRoutine === 'threeFour' || answers.currentRoutine === 'many') ? 'established' :
      answers.recommendationSize === 'decide' ? 'open' : 'neutral';
    var everyday = pick(copy.results.everydaySentences[everydayKey], context.signature, 'everyday');
    return [main, everyday].filter(Boolean).join(' ');
  };

  Ritual.prototype.focusInsight = function (selection, context) {
    if (!selection.selected.length) {
      return {
        title: this.copy.results.noProductsTitle || pick(this.copy.results.focusTitles, context.signature, 'focus-title'),
        text: this.copy.results.noProducts
      };
    }
    var core = selection.selected[0];
    var productCopy = this.copy.products[core.key] || {};
    var product = core.product || {};
    var coreName = product.shortName || product.name || core.key.toUpperCase();
    var purpose = productCopy.purpose || '';
    var purposeSentence = this.phrases.purposeLead + purpose + '.';
    return {
      title: pick(this.copy.results.focusTitles, context.signature, 'focus-title'),
      text: replaceTokens(pick(this.copy.results.focusTexts, context.signature, 'focus-text'), {
        core: coreName,
        purpose: purpose,
        purposeSentence: purposeSentence
      })
    };
  };

  Ritual.prototype.measureInsight = function (context) {
    var key = this.answers.recommendationSize || 'decide';
    var titles = this.copy.results.measureTitles[key] || this.copy.results.measureTitles.decide;
    var texts = this.copy.results.measureTexts[key] || this.copy.results.measureTexts.decide;
    return {
      title: pick(titles, context.signature, 'measure-title'),
      text: pick(texts, context.signature, 'measure-text')
    };
  };

  Ritual.prototype.roleLabel = function (index, context) {
    var role = index === 0 ? 'core' : index === 1 ? 'support' : 'optional';
    return pick(this.copy.ui.roles[role], context.signature, 'role-' + index);
  };

  Ritual.prototype.productMarkup = function (candidate, index, context) {
    var key = candidate.key;
    var product = candidate.product || {};
    var productCopy = this.copy.products[key] || { purpose: '', step: '', reasons: {} };
    var reasonKey = this.productReasonKey(key);
    var reasons = productCopy.reasons && (productCopy.reasons[reasonKey] || productCopy.reasons.fallback);
    var reason = pick(reasons || [], context.signature, 'reason-' + key) || productCopy.purpose || '';
    var image = product.image || product.fallbackImage || '';
    var name = product.name || product.shortName || key.toUpperCase();
    var type = product.type || this.phrases.productFallbackType;
    var url = product.url || (product.handle ? storefrontRoot() + 'products/' + product.handle : '#');
    var fit = product.imageFit || 'contain';
    var position = product.imagePosition || 'center center';
    var variant = this.productVariant(key);
    var cardClass = 'awr-product' + (index === 0 ? ' awr-product--core' : '');

    return '<article class="' + cardClass + '" style="--awr-card-index:' + index + '">' +
      '<div class="awr-product__media">' +
        '<span class="awr-product__index" aria-hidden="true">' + pad(index + 1) + '</span>' +
        (image ? '<img src="' + escapeAttribute(image) + '" alt="' + escapeAttribute(name) + '" loading="lazy" decoding="async" style="object-fit:' + escapeAttribute(fit) + ';object-position:' + escapeAttribute(position) + '">' : '') +
      '</div>' +
      '<div class="awr-product__content">' +
        '<p class="awr-product__role">' + escapeHtml(this.roleLabel(index, context)) + '</p>' +
        '<h4 class="awr-product__name">' + escapeHtml(name) + '</h4>' +
        '<p class="awr-product__type">' + escapeHtml(type) + '</p>' +
        '<p class="awr-product__reason">' + escapeHtml(reason) + '</p>' +
        '<p class="awr-product__step"><strong>' + escapeHtml(this.copy.ui.how) + '</strong>' + escapeHtml(productCopy.step || '') + '</p>' +
        (variant ? '<p class="awr-product__variant">' + escapeHtml(variant) + '</p>' : '') +
        '<div class="awr-product__bottom">' +
          '<span class="awr-product__price">' + escapeHtml(product.price || '') + '</span>' +
          '<span class="awr-product__link" aria-hidden="true">' +
            '<span>' + escapeHtml(this.copy.ui.viewProduct) + '</span>' +
            '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h13M13 7l5 5-5 5"></path></svg>' +
          '</span>' +
        '</div>' +
      '</div>' +
      '<a class="awr-product__card-link" href="' + escapeAttribute(url) + '" aria-label="' + escapeAttribute(replaceTokens(this.copy.ui.openProduct, { product: name })) + '"></a>' +
    '</article>';
  };

  Ritual.prototype.restraintText = function (selection, context) {
    var avoid = toArray(this.answers.avoid);
    var restraint = this.copy.results.restraint;
    var key = null;

    if (!selection.selected.length) key = 'zero';
    else if (avoid.indexOf('manySteps') !== -1) key = 'manySteps';
    else if (avoid.indexOf('strongScent') !== -1 || this.answers.scentTolerance === 'none') key = 'strongScent';
    else if (avoid.indexOf('heavy') !== -1) key = 'heavy';
    else if (avoid.indexOf('visibleMakeup') !== -1) key = 'visibleMakeup';
    else if (avoid.indexOf('dailyObligation') !== -1) key = 'dailyObligation';
    else if (selection.candidates.length > selection.selected.length) key = 'cap';

    return key && restraint[key] ? pick(restraint[key], context.signature, 'restraint-' + key) : '';
  };

  Ritual.prototype.signalLabels = function () {
    var copy = this.copy;
    var answers = this.answers;
    var signals = toArray(answers.focus).slice(0, 2).map(function (item) {
      return summaryLabel(copy, 'focus', item);
    });
    signals.push(summaryLabel(copy, 'dailyTime', answers.dailyTime));
    signals.push(summaryLabel(copy, 'priority', answers.priority));
    if ((answers.stress === 'often' || answers.stress === 'very') && signals.length < 4) {
      signals.push(summaryLabel(copy, 'stress', answers.stress));
    }
    return unique(signals).slice(0, 4);
  };

  Ritual.prototype.renderResults = function () {
    var selection = this.selectRecommendations();
    var context = this.resultContext(selection);
    this.signature = context.signature;

    this.setText('[data-awr-result-eyebrow]', this.copy.results.eyebrow);
    this.setText('[data-awr-result-title]', context.title);
    this.setText('[data-awr-result-lead]', context.lead);

    var signals = this.root.querySelector('[data-awr-signals]');
    if (signals) {
      signals.innerHTML = this.signalLabels().map(function (signal) {
        return '<span class="awr-signal">' + escapeHtml(signal) + '</span>';
      }).join('');
    }

    var observationTitle = pick(
      this.copy.results.observationTitles[context.observationDomain] || this.copy.results.observationTitles.mixed,
      context.signature,
      'observation-title'
    );
    this.renderInsight('observation', this.copy.results.insights.observation, observationTitle, this.observationText(context));

    var focusInsight = this.focusInsight(selection, context);
    this.renderInsight('focus', this.copy.results.insights.focus, focusInsight.title, focusInsight.text);

    var measureInsight = this.measureInsight(context);
    this.renderInsight('measure', this.copy.results.insights.measure, measureInsight.title, measureInsight.text);

    this.setText('[data-awr-recommendation-eyebrow]', this.copy.results.recommendationEyebrow);
    this.setText('[data-awr-recommendation-title]', pick(this.copy.results.recommendationTitles, context.signature, 'recommendation-title'));
    this.setText('[data-awr-recommendation-intro]', pick(this.copy.results.recommendationIntro, context.signature, 'recommendation-intro'));
    this.setText('[data-awr-result-count]', selection.selected.length === 0
      ? (this.copy.ui.zeroRecommendations || replaceTokens(this.copy.ui.manyRecommendations, { count: 0 }))
      : selection.selected.length === 1
        ? this.copy.ui.oneRecommendation
        : replaceTokens(this.copy.ui.manyRecommendations, { count: selection.selected.length }));

    var products = this.root.querySelector('[data-awr-products]');
    if (products) {
      if (selection.selected.length) {
        products.classList.remove('awr-products--empty');
        products.innerHTML = selection.selected.map(function (candidate, index) {
          return this.productMarkup(candidate, index, context);
        }, this).join('');
      } else {
        products.classList.add('awr-products--empty');
        products.innerHTML = '<div class="awr-empty-recommendation">' + escapeHtml(this.copy.results.noProducts) + '</div>';
      }
    }

    var restraintText = this.restraintText(selection, context);
    var restraint = this.root.querySelector('[data-awr-restraint]');
    if (restraint) restraint.hidden = !restraintText;
    this.setText('[data-awr-restraint-eyebrow]', this.copy.results.restraintEyebrow);
    this.setText('[data-awr-restraint-text]', restraintText);

    this.setText('[data-awr-afterword-eyebrow]', this.copy.results.afterEyebrow);
    this.setText('[data-awr-afterword-title]', pick(context.theme.afterTitles, context.signature, 'after-title'));
    this.setText('[data-awr-afterword-text]', pick(this.copy.results.afterTexts, context.signature, 'after-text'));
  };

  Ritual.prototype.renderInsight = function (name, eyebrow, title, text) {
    var insight = this.root.querySelector('[data-awr-insight="' + name + '"]');
    if (!insight) return;
    var eyebrowNode = insight.querySelector('[data-awr-insight-eyebrow]');
    var titleNode = insight.querySelector('[data-awr-insight-title]');
    var textNode = insight.querySelector('[data-awr-insight-text]');
    if (eyebrowNode) eyebrowNode.textContent = eyebrow || '';
    if (titleNode) titleNode.textContent = title || '';
    if (textNode) textNode.textContent = text || '';
  };

  Ritual.prototype.setText = function (selector, value) {
    var node = this.root.querySelector(selector);
    if (node) node.textContent = value || '';
  };

  function initialise(scope) {
    var roots = [];
    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (scope && scope.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll(ROOT_SELECTOR)));
    }
    roots.forEach(function (root) {
      if (root.dataset.awrReady === 'true') return;
      try { new Ritual(root); } catch (error) {
        root.dataset.awrError = 'true';
        if (window.console && console.error) console.error('ANTLITZWERK Ritual:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialise(document); }, { once: true });
  } else {
    initialise(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialise(event.target || document);
  });
})();
